/**
 * Champs `title` / `meta description`, compteurs de caractères et aperçus.
 *
 * Partagé par les écrans de contenu et par l'écran SEO, pour que le même
 * chiffre veuille dire la même chose partout.
 *
 * Deux avertissements ne sont pas cosmétiques :
 *
 * - **Un `metaTitle` vide produit une page sans `<title>`.** Les gabarits Astro
 *   passent la valeur telle quelle à `Base.astro`, sans repli : la page publiée
 *   n'aurait littéralement pas de titre dans l'onglet ni dans les résultats de
 *   recherche.
 * - **`og:title` et `og:description` sont recopiés de ces deux champs.** Il n'y
 *   a pas de valeur OpenGraph distincte à saisir, et laisser croire le
 *   contraire ferait chercher un champ qui n'existe pas.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

/** Origine publique du site, comme dans `landing/src/lib/jsonld.ts`. */
export const SITE_PUBLIC = "https://delsa.pro";

/**
 * Seuils du spec. Au-delà de 65 caractères, Google tronque le titre ; une
 * description hors de 120–165 est soit tronquée, soit trop maigre pour être
 * reprise telle quelle. Ce sont des bornes de rédaction, pas des règles
 * techniques : rien ne casse si on les dépasse, mais le résultat affiché n'est
 * plus celui qu'on a écrit.
 */
export const SEUILS_SEO = {
  titreMax: 65,
  descriptionMin: 120,
  descriptionMax: 165,
} as const;

export type EtatSeo = {
  severite: "success" | "warning" | "error";
  message: string;
};

export function etatTitre(valeur: string): EtatSeo {
  const taille = valeur.trim().length;
  if (taille === 0) {
    return {
      severite: "error",
      message: "Vide : la page serait publiée sans balise <title>.",
    };
  }
  if (taille > SEUILS_SEO.titreMax) {
    return {
      severite: "warning",
      message: `${taille} caractères : au-delà de ${SEUILS_SEO.titreMax}, la fin sera tronquée dans les résultats de recherche.`,
    };
  }
  return { severite: "success", message: `${taille} caractères.` };
}

export function etatDescription(valeur: string): EtatSeo {
  const taille = valeur.trim().length;
  if (taille === 0) {
    return {
      severite: "error",
      message:
        "Vide : le moteur composera lui-même un extrait à partir de la page, sans que vous le choisissiez.",
    };
  }
  if (taille < SEUILS_SEO.descriptionMin) {
    return {
      severite: "warning",
      message: `${taille} caractères : en dessous de ${SEUILS_SEO.descriptionMin}, la description est souvent ignorée au profit d'un extrait automatique.`,
    };
  }
  if (taille > SEUILS_SEO.descriptionMax) {
    return {
      severite: "warning",
      message: `${taille} caractères : au-delà de ${SEUILS_SEO.descriptionMax}, la fin sera coupée.`,
    };
  }
  return { severite: "success", message: `${taille} caractères.` };
}

/** Résume l'état des deux champs pour un pastillage de liste. */
export function seoEnDefaut(metaTitle: string, metaDescription: string): boolean {
  return (
    etatTitre(metaTitle).severite !== "success" ||
    etatDescription(metaDescription).severite !== "success"
  );
}

const COULEUR_ETAT: Record<EtatSeo["severite"], string> = {
  success: "success.main",
  warning: "warning.main",
  error: "error.main",
};

type ProprietesChamps = {
  metaTitle: string;
  metaDescription: string;
  surTitre: (valeur: string) => void;
  surDescription: (valeur: string) => void;
  /** Chemin public de la page, « /peinture/ ». */
  chemin: string;
  erreurTitre?: string;
  erreurDescription?: string;
};

export function ChampsSeo({
  metaTitle,
  metaDescription,
  surTitre,
  surDescription,
  chemin,
  erreurTitre,
  erreurDescription,
}: ProprietesChamps) {
  const titre = etatTitre(metaTitle);
  const description = etatDescription(metaDescription);

  return (
    <Stack spacing={2.5}>
      <Box>
        <TextField
          fullWidth
          label="Titre de la page (balise title)"
          value={metaTitle}
          onChange={(evenement) => surTitre(evenement.target.value)}
          error={erreurTitre !== undefined}
          helperText={erreurTitre}
        />
        <Compteur etat={titre} />
      </Box>

      <Box>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Meta description"
          value={metaDescription}
          onChange={(evenement) => surDescription(evenement.target.value)}
          error={erreurDescription !== undefined}
          helperText={erreurDescription}
        />
        <Compteur etat={description} />
      </Box>

      <ApercuSerp chemin={chemin} titre={metaTitle} description={metaDescription} />
    </Stack>
  );
}

function Compteur({ etat }: { etat: EtatSeo }) {
  return (
    <Typography
      variant="caption"
      sx={{ display: "block", mt: 0.5, color: COULEUR_ETAT[etat.severite] }}
      // Le compteur change à chaque frappe : « polite » le fait annoncer à la
      // pause, pas caractère par caractère.
      aria-live="polite"
    >
      {etat.message}
    </Typography>
  );
}

/**
 * Aperçu du résultat de recherche. Les troncatures reproduisent celles de
 * Google : c'est tout l'intérêt de l'aperçu, montrer ce qui sera réellement lu
 * plutôt que ce qui a été saisi.
 */
export function ApercuSerp({
  chemin,
  titre,
  description,
}: {
  chemin: string;
  titre: string;
  description: string;
}) {
  // Google n'affiche plus l'URL brute mais un fil de segments : « delsa.pro › peinture ».
  const fil = ["delsa.pro", ...chemin.split("/").filter(Boolean)].join(" › ");
  const titreAffiche = tronquer(titre.trim(), SEUILS_SEO.titreMax);
  const descriptionAffichee = tronquer(description.trim(), SEUILS_SEO.descriptionMax);

  return (
    <Paper variant="outlined" sx={{ p: 2, backgroundColor: "background.default" }}>
      <Typography variant="subtitle2" gutterBottom>
        Aperçu dans les résultats de recherche
      </Typography>

      <Box sx={{ fontFamily: "Arial, sans-serif", maxWidth: 600 }}>
        <Typography sx={{ fontSize: "0.8rem", color: "#4d5156" }}>{fil}</Typography>
        <Typography
          sx={{ fontSize: "1.25rem", color: "#1a0dab", lineHeight: 1.3, mt: 0.5 }}
          component="p"
        >
          {titreAffiche || "(sans titre)"}
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: "#4d5156", mt: 0.5 }} component="p">
          {descriptionAffichee ||
            "(sans description : le moteur composera lui-même un extrait de la page)"}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        Les mêmes valeurs alimentent OpenGraph et Twitter : <code>og:title</code> et{" "}
        <code>twitter:title</code> reprennent le titre, <code>og:description</code> la description.
        L'image de partage (<code>og:image</code>) n'est pas encore gérée par page.
      </Typography>
    </Paper>
  );
}

/** Coupe au dernier espace avant la limite, comme le fait un moteur. */
function tronquer(texte: string, maximum: number): string {
  if (texte.length <= maximum) return texte;
  const coupe = texte.slice(0, maximum);
  const espace = coupe.lastIndexOf(" ");
  return `${(espace > maximum * 0.6 ? coupe.slice(0, espace) : coupe).trimEnd()}…`;
}

/** Rappel affiché en tête de l'écran SEO. */
export function RappelSeo() {
  return (
    <Alert severity="info">
      Le titre et la description sont recopiés tels quels dans la page publiée : ni les gabarits, ni
      l'API ne fabriquent de repli. Une valeur vide donne une page sans titre ou sans description.
    </Alert>
  );
}
